import { motion } from "motion/react";
import { IconFileTypePdf, IconMapPin } from "@tabler/icons-react";
import {
  CongressImageLightboxHost,
  MORPH_EASE,
  MORPH_SECONDS,
} from "../room/CongressImageLightbox";
import {
  cardImageSrc,
  cropStyle,
  lightboxSrc,
  type DocumentCrop,
  type DocumentImageSizes,
} from "../../domains/congress/documentImage";
import { useDocumentLightbox } from "../../domains/congress/useDocumentLightbox";
import { usePdfFirstPage } from "../../domains/congress/pdfFirstPage";

/**
 * What the cover shows. `pdf`: the partner's thumbnail, else the first page of
 * the file, else the document icon on lavender. `image`: the image itself,
 * framed by the admin's crop; the card opens it in its own lightbox.
 */
export type DesktopCongressDocCover =
  | {
      kind: "pdf";
      thumbnailUrl: string | null;
      thumbnailSizes: DocumentImageSizes;
      pdfUrl: string | null;
    }
  | {
      kind: "image";
      imageUrl: string | null;
      imageSizes: DocumentImageSizes;
      crop: DocumentCrop;
      /** Shared with the lightbox so motion morphs between the two. */
      layoutId: string;
    };

const CARD_CLASS =
  "group relative flex w-full cursor-pointer flex-col gap-2.5 border-none bg-transparent p-0 text-left font-dm-sans focus-visible:outline-3 focus-visible:outline-offset-3 focus-visible:outline-focus-ring";

/**
 * A document of a congress space on desktop (Figma DS "CongressDocCard"): the
 * anatomy of the collection card, so both live in the same grids. A 184 high
 * cover with the document type over it, the title, then the detail line.
 */
export function DesktopCongressDocCard({
  label,
  title,
  meta,
  cover,
  onOpen,
}: {
  /** The type over the cover, e.g. "FLOORPLAN · PDF" */
  label: string;
  title: string;
  /** The detail line; `place` adds the pin, for a stand or a booth. */
  meta: { text: string; place: boolean };
  cover: DesktopCongressDocCover;
  /** A PDF: where it opens. An image: analytics only (it opens its lightbox). */
  onOpen: () => void;
}): React.JSX.Element {
  const body = (
    <>
      <p className="m-0 line-clamp-3 text-desktop-card-title font-semibold text-ds-black group-hover:underline">
        {title}
      </p>
      <p className="m-0 flex items-center gap-1 text-desktop-meta text-ds-slate-600">
        {meta.place ? (
          <IconMapPin className="size-3.5 shrink-0" stroke={1.5} />
        ) : null}
        <span className="min-w-0 flex-1">{meta.text}</span>
      </p>
    </>
  );
  switch (cover.kind) {
    case "pdf":
      return (
        <button type="button" onClick={onOpen} className={CARD_CLASS}>
          <PdfCover cover={cover} label={label} />
          {body}
        </button>
      );
    case "image":
      return (
        <ImageDocCard cover={cover} label={label} title={title} onOpen={onOpen}>
          {body}
        </ImageDocCard>
      );
    default:
      throw new Error(cover satisfies never);
  }
}

function CoverFrame({
  label,
  isPdf,
  children,
}: {
  label: string;
  isPdf: boolean;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="relative h-46 w-full shrink-0 overflow-hidden rounded-xl bg-ds-blackberry-50">
      {children}
      {/* The hover veil (6 %), under the type chip. */}
      <div
        aria-hidden
        className="absolute inset-0 bg-ds-black opacity-0 transition-opacity group-hover:opacity-6"
      />
      <span className="absolute top-3 left-3 flex items-center gap-1 rounded-md bg-ds-white py-1 pr-2 pl-1.5 text-caption font-semibold text-ds-blackberry-600 uppercase">
        {isPdf ? (
          <IconFileTypePdf className="size-4 shrink-0" stroke={1.5} />
        ) : null}
        {label}
      </span>
    </div>
  );
}

function PdfCover({
  cover,
  label,
}: {
  cover: Extract<DesktopCongressDocCover, { kind: "pdf" }>;
  label: string;
}): React.JSX.Element {
  // The partner's thumbnail wins; the file's first page renders only without.
  const firstPage = usePdfFirstPage(
    cover.thumbnailUrl === null ? cover.pdfUrl : null
  );
  const imageUrl =
    cover.thumbnailUrl === null
      ? firstPage
      : cardImageSrc(cover.thumbnailUrl, cover.thumbnailSizes);
  return (
    <CoverFrame label={label} isPdf={true}>
      {imageUrl === null ? (
        <span className="flex size-full items-center justify-center text-ds-blackberry-600">
          <IconFileTypePdf className="size-8" stroke={1.25} />
        </span>
      ) : (
        <img
          src={imageUrl}
          alt=""
          className="size-full bg-ds-white object-cover object-top"
        />
      )}
    </CoverFrame>
  );
}

function ImageDocCard({
  cover,
  label,
  title,
  onOpen,
  children,
}: {
  cover: Extract<DesktopCongressDocCover, { kind: "image" }>;
  label: string;
  title: string;
  onOpen: () => void;
  children: React.ReactNode;
}): React.JSX.Element {
  const {
    imageRef,
    open,
    aspectRatio,
    exitInPlace,
    openLightbox,
    closeLightbox,
  } = useDocumentLightbox(cover.imageSizes);
  const { imageUrl, imageSizes } = cover;
  return (
    <>
      <button
        type="button"
        onClick={() => {
          onOpen();
          openLightbox();
        }}
        className={CARD_CLASS}
      >
        <CoverFrame label={label} isPdf={false}>
          {imageUrl === null ? null : (
            <motion.img
              ref={imageRef}
              layoutId={cover.layoutId}
              src={cardImageSrc(imageUrl, imageSizes)}
              alt={title}
              style={cropStyle(cover.crop)}
              // The image that travels back on close: same timing as the
              // opening morph (see CongressImageCard).
              transition={{
                layout: { duration: MORPH_SECONDS, ease: MORPH_EASE },
              }}
            />
          )}
        </CoverFrame>
        {children}
      </button>
      <CongressImageLightboxHost
        open={open}
        cardSrc={imageUrl === null ? null : cardImageSrc(imageUrl, imageSizes)}
        originalSrc={
          imageUrl === null ? null : lightboxSrc(imageUrl, imageSizes)
        }
        aspectRatio={aspectRatio}
        alt={title}
        layoutId={cover.layoutId}
        exitInPlace={exitInPlace}
        onClose={closeLightbox}
      />
    </>
  );
}
